import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";

//
import FormInput from "./FormInput";
import { SuccessModal } from "@/components/Organisms/CallbackModalNew";
//
import { generateUrlArguments, timezone } from "@/utils";
import { DEFAULT_URL_ARGUMENTS } from "@/utils/Constants";

import {
  useAppDispatch,
  useAppSelector,
  useRazorPay,
  useRegisterForm,
} from "@/hooks";
import * as actions from "@/redux/LandingPageReducer";

//
function NutritionForm({ data, setIsPaymentSuccessful }) {
  const {
    title,
    subtitle,
    //
    showNameField = true,
    showEmailField,
    showPhoneField = true,
    showGoalField,
    showPackageField,
    showStartDateField,
    showPrimaryCTA = true,
    showRequestCallback,
    showTermsAndConditions,
    //
    namePlaceHolder,
    emailPlaceholder,
    mobilePlaceholder,
    goalPlaceholder,
    //
    gtmIdHealthGoal,
    gtmIdPrimaryCTA,
    gtmIdSinglePackage,
    gtmIdPackages,
    //
    goalFieldLabel,
    packageFieldLabel,
    startDateLabel,
    //
    primaryCTAText,
    isPrimaryCTAWithCallback,
    isPrimaryCTAWithPrice,
    secondaryCTAText,
    //
    goalOptions,
    packageOptions,
    //
    pageType,
    purchaseEventType,
    defaultGoal,
    defaultPackage: _defaultPackage,
    defaultStartDateOffset,
    //
    setUserMobileNumber,
  } = data || {};

  const dispatch = useAppDispatch();

  const router = useRouter();
  const urlParams = router?.search || "";

  const defaultPackageSlug = router?.query?.default_slug;

  const defaultPackage = _defaultPackage || defaultPackageSlug;

  const { leadSuccess: successLead } =
    useAppSelector((state) => state.landingPage) || {};
  const { countryCodeData } = useAppSelector((state) => state.dashboard);

  const {
    isPaymentLoading: isLoading,
    paymentError: error,
    paymentSuccess: success,
  } = useAppSelector((state) => state.landingPage);

  const [openStartDateModal, setOpenStartDateModal] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [showCallbackSuccessView, setShowCallbackSuccessView] = useState(false);
  const [hasRequestedForCallback, setHasRequestedForCallback] = useState(false);

  useEffect(() => {
    let successModelTimeout = null;

    if (successLead && hasRequestedForCallback) {
      setShowCallbackSuccessView(true);
      successModelTimeout = setTimeout(() => {
        setShowCallbackSuccessView(false);
        setHasRequestedForCallback(false);
      }, 60000);
    }

    return () => {
      clearTimeout(successModelTimeout);
    };
  }, [successLead, hasRequestedForCallback]);
  const {
    handleBlur,
    handleChange,
    handleSelectChange,
    onSubmitCallback,
    inputs,
    resetFormInputs,
  } = useRegisterForm({
    ...(showNameField && {
      name: "",
    }),
    ...(showEmailField && {
      email: "",
    }),
    ...(showPhoneField && {
      countryCode: countryCodeData?.default?.code || "+91",
      mobile: "",
    }),
    ...(showGoalField && {
      goal: defaultGoal || "",
    }),
    ...(showPackageField && {
      selectedPackageDuration: defaultPackage || "",
    }),
  });

  const {
    name,
    email,
    countryCode,
    mobile,
    goal,
    selectedPackageDuration,
    nameError,
    emailError,
    countryCodeError,
    mobileError,
    goalError,
    selectedPackageDurationError,
  } = inputs;

  const isError =
    nameError ||
    emailError ||
    countryCodeError ||
    mobileError ||
    goalError ||
    selectedPackageDurationError;

  useEffect(() => {
    if (isError) {
      const errors = document.querySelectorAll(".form-input-error");
      // eslint-disable-next-line no-unused-expressions
      errors?.[0]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [isError]);

  const pack = packageOptions?.find(
    (p) =>
      p?.packageSlug === selectedPackageDuration ||
      p?.packageSlug === defaultPackage
  );

  const defaultSlug = data?.packageSlug || "nutrition-callback-others";

  const urlsArguments = generateUrlArguments(urlParams);

  const packageSlug = goal || defaultGoal || defaultSlug;

  const onBlur = (event) => {
    const field = event?.target?.name;
    const value = event?.target?.value;

    const isSpecialCourse =
      packageOptions?.find((p) => p?.packageSlug === value)?.courseType ===
      "SPECIALIZED";

    const getEventType = () => {
      switch (field) {
        case "goal":
          return {
            event_type: "health-goal-selected",
            event_value: value?.slug,
          };
        case "selectedPackageDuration":
          return {
            event_type: isSpecialCourse
              ? "single-consultation-package-selected"
              : "package-duration-selected",
            event_value: value,
          };
        case "startDate":
          return {
            event_type: "start-date-selected",
            event_value: value,
          };
        case "payment":
          return {
            event_type: "pay-now-clicked",
            event_value: "Package payment pending",
          };
        case "payment-failed":
          return {
            event_type: "pay-now-clicked",
            event_value: "Package payment failed",
          };
        default:
          return "";
      }
    };

    const eventType = getEventType();

    if (eventType && !eventType?.event_value) return;

    const apiData = {
      name,
      email,
      phone: mobile,
      phone_prefix: countryCode || "+91",
      package_slug: packageSlug,
      timezone,
      urls_arguments: urlsArguments,
      is_lead: true,
      page_type: pageType,
      ...(eventType && { ...eventType }),
    };

    return dispatch(actions.saveLead({ data: apiData }));
  };

  const onDismiss = () => {
    dispatch(
      actions.saveLead({
        data: {
          name,
          email,
          phone: mobile,
          phone_prefix: countryCode || "+91",
          package_slug: packageSlug,
          timezone,
          urls_arguments: urlsArguments,
          payment_mode: "razorpay",
          payment_stage: "payment_cancel",
          event_type: "pay-now-clicked",
          event_value: "Package payment cancel",
          lead_via_partner_website: false,
        },
      })
    );
  };

  const onSuccessCallback = (response) => {
    let date = new Date();

    if (defaultStartDateOffset)
      date.setDate(date.getDate() + Number(defaultStartDateOffset));

    const year = date.toLocaleString("default", { year: "numeric" });
    const month = date.toLocaleString("default", { month: "2-digit" });
    const day = date.toLocaleString("default", { day: "2-digit" });

    const formattedDate = `${year}-${month}-${day}`;

    const eventType = purchaseEventType;

    const apiData = {
      name,
      phone_prefix: countryCode || "+91",
      mobile,
      email,
      timezone,
      package_slug: packageSlug,
      page_type: pageType,
      is_payment_completed: true,
      payment_mode: "razorpay",
      transaction_id: response?.razorpay_payment_id,
      purchased_plan_slug: pack?.packageSlug,
      package_start_date: startDate || formattedDate,
      urls_arguments: urlsArguments || DEFAULT_URL_ARGUMENTS,
      is_callback_request: false,
      amount_paid: pack?.discountedPrice,
      event_type: eventType,
      event_value: "Package purchased",
    };

    dispatch(actions.postPackageSave({ router, data: apiData }))
      .then(() => {
        setIsPaymentSuccessful(true);
        setUserMobileNumber(apiData?.mobile);
      })
      .catch(() => {
        onBlur({
          name: "payment-failed",
          value: "payment-failed",
        });
        router?.push({
          pathname: process.env.NEXT_PUBLIC_LOCATION + "payments/failure",
        });
      });
  };

  const { openPaymentModal, razorPayError } = useRazorPay({
    name,
    countryCode,
    mobile,
    email,
    onDismiss,
    onSuccessCallback,
  });

  const openPaymentPage = ({ currency, price, duration }) => {
    let date = new Date();

    if (defaultStartDateOffset)
      date.setDate(date.getDate() + Number(defaultStartDateOffset));

    const year = date.toLocaleString("default", { year: "numeric" });
    const month = date.toLocaleString("default", { month: "2-digit" });
    const day = date.toLocaleString("default", { day: "2-digit" });

    const formattedDate = `${year}-${month}-${day}`;
    const notesData = {
      package_slug: packageSlug,
      page_type: pageType,
      purchased_plan_slug: pack?.packageSlug,
      package_start_date: startDate || formattedDate,
      urls_arguments: urlsArguments || DEFAULT_URL_ARGUMENTS,
    };
    openPaymentModal(
      price,
      currency,
      notesData?.purchased_plan_slug,
      () => {},
      notesData
    );
  };

  const onSubmit = () => {
    handleInputBlur({
      target: { name: "payment", value: "payment" },
    });

    const _paymentData = {
      currency: pack?.priceConstant || "INR",
      price: Number(pack?.discountedPrice),
      duration: `${pack?.duration} Months`,
    };

    openPaymentPage(_paymentData);
  };

  const onSubmitCallbackRequest = () => {
    const apiData = {
      name,
      email,
      phone: mobile,
      phone_prefix: countryCode || "+91",
      package_slug: packageSlug,
      timezone,
      urls_arguments: urlsArguments,
      page_type: pageType,
      is_callback_request: true,
    };

    return dispatch(actions.saveLead({ data: apiData })).then(() => {
      setHasRequestedForCallback(true);
      resetFormInputs();
    });
  };

  const onSubmitButtonClick = (e, callbackRequest) => {
    e.preventDefault();
    if (callbackRequest) {
      onSubmitCallback(true, onSubmitCallbackRequest);
    } else {
      onSubmitCallback(true, onSubmit);
    }
  };

  const handleInputBlur = (e) => handleBlur(e, onBlur);

  return (
    <>
      {showCallbackSuccessView && (
        <SuccessModal
          onHideNutritionCallbackModal={() => {}}
          showBtn={false}
          data={{ ...data, hero: { show_app_download_links: true } }}
        />
      )}
      {!showCallbackSuccessView && (
        <FormInput
          {...inputs}
          //
          isLoading={isLoading}
          success={success}
          //
          handleChange={handleChange}
          handleSelectChange={handleSelectChange}
          handleInputBlur={handleInputBlur}
          onSubmitButtonClick={onSubmitButtonClick}
          //
          title={title}
          subtitle={subtitle}
          //
          showNameField={showNameField}
          namePlaceHolder={namePlaceHolder}
          //
          showEmailField={showEmailField}
          emailPlaceholder={emailPlaceholder}
          //
          showPhoneField={showPhoneField}
          mobilePlaceholder={mobilePlaceholder}
          //
          showGoalField={showGoalField}
          goalFieldLabel={goalFieldLabel}
          goalPlaceholder={goalPlaceholder}
          goalOptions={goalOptions}
          gtmIdHealthGoal={gtmIdHealthGoal}
          //
          showPackageField={showPackageField}
          packageFieldLabel={packageFieldLabel}
          packageOptions={packageOptions}
          gtmIdPackages={gtmIdPackages}
          gtmIdSinglePackage={gtmIdSinglePackage}
          //
          showStartDateField={showStartDateField}
          startDateLabel={startDateLabel}
          startDate={startDate}
          setStartDate={setStartDate}
          openStartDateModal={openStartDateModal}
          setOpenStartDateModal={setOpenStartDateModal}
          //
          showPrimaryCTA={showPrimaryCTA}
          primaryCTAText={primaryCTAText}
          isPrimaryCTAWithPrice={isPrimaryCTAWithPrice}
          gtmIdPrimaryCTA={gtmIdPrimaryCTA}
          //
          showRequestCallback={showRequestCallback}
          secondaryCTAText={secondaryCTAText}
          //
          showTermsAndConditions={showTermsAndConditions}
          //
          defaultPackage={defaultPackage}
          isPrimaryCTAWithCallback={isPrimaryCTAWithCallback}
        />
      )}
    </>
  );
}

export default NutritionForm;
